import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import { NNotifications } from '../interfaces/services/notifications.interfaces'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { sortByDate } from '../helpers/sorting'

const prefix = '/notifications'

export const list = (
  query?: NNotifications.NList.IRequestQuery
): Promise<AxiosResponse<NNotifications.NList.IResponse>> => {
  return axiosAuth.get<NNotifications.NList.IResponse>(
    createUrl(`${prefix}/list`, {
      ...query,
    })
  )
}

export const getUserUnseenUnreadCount = (): Promise<
  AxiosResponse<NNotifications.NGetUserUnreadUnseenCount.IResponse>
> => {
  return axiosAuth.get<NNotifications.NGetUserUnreadUnseenCount.IResponse>(
    createUrl(`${prefix}/get-user-unread-unseen-count`)
  )
}

export const getNotification = (
  id: string
): Promise<AxiosResponse<NNotifications.NList.IResponse>> => {
  return axiosAuth.get<NNotifications.NList.IResponse>(
    createUrl(`${prefix}/notification/${id}`)
  )
}

export const setRead = (
  body: NNotifications.NSetRead.IRequestBody
): Promise<AxiosResponse<NNotifications.NSetRead.IResponse>> => {
  return axiosAuth.post<NNotifications.NSetRead.IResponse>(
    createUrl(`${prefix}/set-read`),
    body
  )
}

export const setSeen = (
  body: NNotifications.NSetSeen.IRequestBody
): Promise<AxiosResponse<NNotifications.NSetSeen.IResponse>> => {
  return axiosAuth.post<NNotifications.NSetSeen.IResponse>(
    createUrl(`${prefix}/set-seen`),
    body
  )
}

export const setTypesSeen = (
  body: NNotifications.NSetTypesSeen.IRequestBody
): Promise<AxiosResponse<NNotifications.NSetTypesSeen.IResponse>> => {
  return axiosAuth.post<NNotifications.NSetTypesSeen.IResponse>(
    createUrl(`${prefix}/set-types-seen`),
    body
  )
}

function parseUnseenObject(unseenObject: { [key: string]: number }): {
  notificationScreenUnseenCount: number
  oneOnOneCount: number
  interestsCount: number
  boardCount: number
  boardQuestionsCount: number
  residentQuestionsCount: number
} {
  const unseenObjectKeys = Object.keys(unseenObject)

  const notificationScreenFilterKeys = [
    'chat-oneonone-new-message',
    'chat-interests-new-message',
    'chat-board-new-message',
    'chat-questions-new-message-board',
    'chat-questions-new-message-residents',
    'user-added-to-unit',
    'invite-all-admin-to-society',
    'new-ticket-message',
    'new-ticket-message-from-board',
  ]

  const notificationScreenUnseenCount = unseenObjectKeys
    .filter((key) => !notificationScreenFilterKeys.includes(key))
    .map((key) => unseenObject[key])
    .reduce((a, b) => a + b, 0)

  const oneOnOneCount = unseenObject['chat-oneonone-new-message'] ?? 0

  const interestsCount = unseenObject['chat-interests-new-message'] ?? 0

  const boardCount = unseenObject['chat-board-new-message'] ?? 0

  const boardQuestionsCount =
    unseenObject['chat-questions-new-message-board'] ?? 0

  const residentQuestionsCount =
    unseenObject['chat-questions-new-message-residents'] ?? 0

  return {
    notificationScreenUnseenCount,
    oneOnOneCount,
    interestsCount,
    boardCount,
    boardQuestionsCount,
    residentQuestionsCount,
  }
}

export const notificationsQueries = {
  list: (query?: NNotifications.NList.IRequestQuery) =>
    queryOptions({
      queryKey: [{ scope: 'notification', entity: 'list', query }],
      queryFn: async ({ queryKey }) => {
        const resp = (await list(queryKey[0].query)).data.data
        resp?.sort((a, b) => sortByDate(a.createdAt, b.createdAt, true))
        return resp
      },
    }),
  getUserUnseenUnreadCount: (userId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'notification', entity: 'userUnseenUnread', userId }],
      enabled: !!userId,
      queryFn: async () => {
        const { data } = (await getUserUnseenUnreadCount()).data
        return {
          ...data,
          unseen: parseUnseenObject(data.unseen),
        }
      },
    }),
}
